import { useState } from 'react';
import { apiPost } from '../api/client';

interface MagicLinkResponse {
  ok: boolean;
  reason?: 'ok' | 'rate_limited' | 'invalid_invite' | 'needs_invite' | 'send_failed';
}

export type LoginInitialError =
  | 'missing'
  | 'invalid'
  | 'expired'
  | 'used'
  | 'missing_invite'
  | 'invite_exhausted';

interface LoginProps {
  initialError?: LoginInitialError | null;
}

const URL_ERROR_TEXT: Record<LoginInitialError, string> = {
  missing: '这个入口少了一段信息,重新发一封就可以。',
  invalid: '这个入口暂时没对上,重新发一封新的就好。',
  expired: '这个入口过期了(15 分钟有效),重新发一封就可以。',
  used: '这个入口已经用过了。为了安全,可以重新发一个新的入口。',
  missing_invite: '邀请码记忆已经过期了(30 分钟),请重新输入邀请码再发送。',
  invite_exhausted: '邀请码刚好用完了。换一个新的邀请码再试。',
};

const SEND_REASON_TEXT: Record<NonNullable<MagicLinkResponse['reason']>, string> = {
  ok: '',
  rate_limited: '同一邮箱每小时只能发 3 次,等一会儿再试。',
  invalid_invite: '这个邀请码暂时没对上,或者已经用完了。',
  needs_invite: '', // handled separately: switches the form into "new user" mode
  send_failed: '邮件暂时没送出去,过几秒再试一次。',
};

// Cached so the magic-link error page (sessionStorage above) can offer
// "重发一封到 X" without making the user retype their email.
const EMAIL_CACHE_KEY = 'gime5.lastEmail.v1';

function readCachedEmail(): string {
  if (typeof window === 'undefined') return '';
  return sessionStorage.getItem(EMAIL_CACHE_KEY) ?? '';
}

function writeCachedEmail(email: string): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(EMAIL_CACHE_KEY, email);
}

export function Login({ initialError }: LoginProps) {
  const [email, setEmail] = useState(() => readCachedEmail());
  const [inviteCode, setInviteCode] = useState('');
  // 'new_user' mode appears AFTER the server tells us this email isn't known yet.
  // It reveals the inviteCode field inline; users with valid codes can finish
  // signup without leaving the page.
  const [needsInvite, setNeedsInvite] = useState(false);
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(
    initialError ? URL_ERROR_TEXT[initialError] : null,
  );

  async function sendMagicLink(targetEmail: string, code?: string): Promise<void> {
    setPending(true);
    setError(null);
    try {
      const data = await apiPost<MagicLinkResponse>('/api/auth/magic-link', {
        email: targetEmail,
        ...(code ? { inviteCode: code } : {}),
      });
      if (data.ok) {
        writeCachedEmail(targetEmail);
        setSent(targetEmail);
      } else if (data.reason === 'needs_invite') {
        // Server signaled this is a new email — switch to invite-code mode.
        setNeedsInvite(true);
      } else {
        setError(SEND_REASON_TEXT[data.reason ?? 'send_failed']);
      }
    } catch {
      setError('网络暂时没拼上,等几秒再试一次。');
    } finally {
      setPending(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || pending) return;
    if (needsInvite && !inviteCode.trim()) return;
    await sendMagicLink(email.trim(), needsInvite ? inviteCode.trim() : undefined);
  }

  if (sent) {
    return (
      <section className="panel artifact">
        <span className="tiny-label">
          <i className="dot amber" /> 登录链接已发到邮箱
        </span>
        <h1>去 {sent} 查收一下</h1>
        <p className="muted">
          点邮件里的按钮就回到桌面,15 分钟内有效。没收到的话,看一下垃圾邮件文件夹。
        </p>
        <div className="actions">
          <button className="btn-ghost" type="button" onClick={() => setSent(null)}>
            换个邮箱
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="panel wish">
      <h1>{needsInvite ? '看起来是第一次来' : '用邮箱进入桌面'}</h1>
      <p className="muted">
        {needsInvite
          ? '我们没找到这个邮箱的账号——加一个邀请码就可以注册进来。邀请码可以从 @赛博神经 / 朋友邀请 拿到。'
          : '填邮箱,我们会发一封带登录链接的邮件给你。老用户直接用之前的邮箱进就好,所有卡片都还在。'}
      </p>
      <form className="form-row" onSubmit={submit}>
        <input
          className="text-input"
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            // If the user edits the email after seeing "needs invite", reset back
            // to email-only mode — the new email might already be a returning user.
            if (needsInvite) setNeedsInvite(false);
          }}
          autoFocus={!needsInvite}
          disabled={pending}
          required
        />
        {needsInvite && (
          <input
            className="text-input"
            type="text"
            placeholder="邀请码,比如 G5-VIVI-2026"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            autoFocus
            disabled={pending}
            maxLength={40}
            style={{ marginTop: 8 }}
          />
        )}
        <button
          className="btn-lime"
          type="submit"
          disabled={pending || !email.trim() || (needsInvite && !inviteCode.trim())}
        >
          {pending ? '正在处理…' : needsInvite ? '注册并发送链接' : '把入口发到邮箱'}
        </button>
      </form>
      {error && (
        <p className="error-line">
          <i className="dot amber" /> {error}
        </p>
      )}
    </section>
  );
}
